import { MIN_SCANNABLE_MODULE_DOTS } from '@sitzmark/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Barcode } from '../../components/barcode.js';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import { agentStatus, printAgentEnabled, sendLabels } from '../../lib/print-agent.js';
import {
  LABEL_SIZES,
  codeFor,
  labelStyleFor,
  pageRuleFor,
  unscannableCodes,
} from './label-geometry.js';
import type { LabelItem, LabelSize } from './label-geometry.js';
import { labelRunZpl, unscannableOnAgent, zplSupports } from './label-zpl.js';
import './labels.css';

export type { LabelSize } from './label-geometry.js';

/**
 * Label printing (SCOPE §7).
 *
 * "From any PO/RO, generate the full label run — one label per unit received (or
 * per line, selectable)." Default is per unit, because that is what goes on the
 * merchandise; per line is for a bin or a rack tag.
 *
 * The barcode is rendered as real Code 128 bars, not a picture of a barcode. At
 * the bench an unscannable label is indistinguishable from a broken one, and you
 * find out one item at a time at the register.
 *
 * **This is now the shop's main route to a scannable item, not a finishing
 * touch.** 90.4% of their imported catalog carries no UPC, so for nine items in
 * ten the label is the barcode — which is why the run is checked against the
 * stock it is going onto before it is sent (`unscannableCodes`), and why the
 * dialog lives here rather than in the receiving bench that used to be the only
 * screen that could reach it.
 */

// Defined beside the geometry so the ZPL builder (label-zpl.ts) and this
// component share one definition without importing each other.
export { codeFor } from './label-geometry.js';
export type { LabelItem } from './label-geometry.js';

export function Label({
  item,
  showPrice,
  showBothPrices,
  storeName = null,
}: {
  item: LabelItem;
  showPrice: boolean;
  showBothPrices: boolean;
  /** Printed small across the top — "Sitzmark Sports" (FB-1114). */
  storeName?: string | null;
}) {
  const code = codeFor(item);

  return (
    <div className={storeName === null ? 'label' : 'label label--with-store'}>
      {storeName === null ? null : <div className="label__store">{storeName}</div>}
      {/*
        Name on its own line, then size and colour on theirs (item 37).
        It was one clamped two-line blob reading `name · colour · size`, so on
        anything with a long name the size — the thing somebody is looking for
        on a rail of the same jacket — was the part that got clipped. Size leads
        the second line for the same reason: it is what the label is read for.
      */}
      <div className="label__name">{item.name}</div>
      {item.detail === '' ? null : <div className="label__detail">{item.detail}</div>}
      <Barcode value={code} className="label__barcode" />
      <div className="label__foot">
        <span className="label__code">{code}</span>
        {showPrice ? (
          <span className="label__price">
            {money(item.retailCents)}
            {showBothPrices && item.cardPriceCents != null ? (
              <span className="label__card-price"> / {money(item.cardPriceCents)} card</span>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export interface LabelRunOptions {
  readonly size: LabelSize;
  readonly showPrice: boolean;
  readonly showBothPrices: boolean;
  /** One label per unit received, or one per line (SCOPE §7). */
  readonly perUnit: boolean;
  /** The shop's name across the top, from Settings; null prints none (FB-1114). */
  readonly storeName?: string | null;
}

export function useLabelPrinter() {
  const [run, setRun] = useState<{
    items: readonly LabelItem[];
    options: LabelRunOptions;
  } | null>(null);

  /** Set once the document is in the DOM, so printing happens after React has drawn it. */
  const pending = useRef(false);

  const print = useCallback((items: readonly LabelItem[], options: LabelRunOptions) => {
    pending.current = true;
    flushSync(() => setRun({ items, options }));
  }, []);

  /*
   * The print happens here rather than on the line after `setRun`.
   *
   * `flushSync` is right from a click handler and **silently wrong from inside a
   * render** — React will not flush while it is already rendering, so the update
   * is deferred and `window.print()` is handed an empty page. That is exactly
   * how auto-print shipped a blank receipt on 2026-08-28. Labels have only ever
   * been reached from a button, so they were safe; they are reachable from two
   * screens now and this costs nothing.
   */
  useEffect(() => {
    if (run === null || !pending.current) return;
    pending.current = false;

    const style = document.createElement('style');
    style.textContent = pageRuleFor(run.options.size);
    document.head.appendChild(style);

    try {
      window.print();
    } finally {
      style.remove();
      setRun(null);
    }
  }, [run]);

  const frame =
    run === null
      ? null
      : createPortal(
          <div
            className={`print-only labels labels--${run.options.size}`}
            style={labelStyleFor(run.options.size)}
          >
            {run.items.flatMap((item, itemIndex) =>
              Array.from({ length: run.options.perUnit ? item.quantity : 1 }, (_unused, copy) => (
                <Label
                  key={`${itemIndex}-${copy}`}
                  item={item}
                  showPrice={run.options.showPrice}
                  showBothPrices={run.options.showBothPrices}
                  storeName={run.options.storeName ?? null}
                />
              )),
            )}
          </div>,
          document.body,
        );

  /** How many labels a run will produce, so the count can be confirmed first. */
  const countFor = (items: readonly LabelItem[], perUnit: boolean): number =>
    perUnit ? items.reduce((total, item) => total + item.quantity, 0) : items.length;

  return { print, frame, countFor };
}

// ---------------------------------------------------------------------------

/**
 * Confirm the count before printing — a label run is hard to take back (§7).
 *
 * Shared by the receiving bench and the Stock screen. It was inside
 * `receiving-screen.tsx` while that was the only caller, and the second caller
 * is exactly when a private copy becomes two that drift: `.chip`, `.toggle`,
 * `TENDER_LABELS` and `NewCustomerDialog` have each taught that here already.
 */
export function LabelRunDialog({
  items,
  size,
  showPrice,
  showBothPrices,
  printer,
  onClose,
  oneOfEach = false,
}: {
  items: readonly LabelItem[];
  size: LabelSize;
  showPrice: boolean;
  showBothPrices: boolean;
  printer: ReturnType<typeof useLabelPrinter>;
  onClose: () => void;
  /**
   * Every item is a single one — a consigned item (decision 131) — so "per
   * unit or per item" asks nothing, and is not shown (§2.2-13).
   */
  oneOfEach?: boolean;
}) {
  const [perUnit, setPerUnit] = useState(true);
  const count = printer.countFor(items, perUnit);
  const run = useLabelRun(printer, size, onClose);
  const options = { size, showPrice, showBothPrices, perUnit };

  return (
    <Modal
      open
      title="Print labels"
      description={`${LABEL_SIZES[size].label} stock, from your store settings.`}
      onClose={onClose}
      {...run.actions(count, () => run.print(items, options, count))}
    >
      <LabelRunFeedback
        run={run}
        count={count}
        onBrowser={() => run.throughBrowser(items, options)}
      />
      {oneOfEach ? null : (
        <>
          <label className="toggle">
            <input
              type="radio"
              name="label-mode"
              checked={perUnit}
              onChange={() => setPerUnit(true)}
            />
            <span>
              One per unit
              <span className="text-secondary"> — goes on the merchandise</span>
            </span>
          </label>
          <label className="toggle">
            <input
              type="radio"
              name="label-mode"
              checked={!perUnit}
              onChange={() => setPerUnit(false)}
            />
            <span>
              One per item
              <span className="text-secondary"> — for a bin or rack tag</span>
            </span>
          </label>
        </>
      )}

      <ScannabilityNotice items={items} size={size} throughAgent={run.route.kind === 'agent'} />
    </Modal>
  );
}

/**
 * One label run, whichever way it prints (decision 130).
 *
 * Shared by the receiving bench's dialog and the style's, so the two cannot
 * come to disagree about when the agent is used or what a failure says — two
 * screens print labels, and a behavior only one of them has is the shape this
 * codebase keeps paying for.
 */
export function useLabelRun(
  printer: ReturnType<typeof useLabelPrinter>,
  size: LabelSize,
  onClose: () => void,
) {
  const route = useLabelRoute(size);
  const send = useMutation({
    mutationFn: (job: { zpl: string; document: string }) => sendLabels(job.zpl, job.document),
  });

  // The name every label carries comes from Settings, so no screen that
  // prints labels has to remember to pass it (FB-1114).
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get.query(),
    staleTime: 5 * 60_000,
  });
  const named = (options: LabelRunOptions): LabelRunOptions => ({
    ...options,
    storeName: settings.data?.label.storeName ?? null,
  });

  const throughBrowser = (items: readonly LabelItem[], options: LabelRunOptions) => {
    printer.print(items, named(options));
    onClose();
  };

  const print = (items: readonly LabelItem[], options: LabelRunOptions, count: number) => {
    if (route.kind !== 'agent') {
      throughBrowser(items, options);
      return;
    }
    send.mutate({ zpl: labelRunZpl(items, named(options)), document: labelsWord(count) });
  };

  /** The dialog's buttons: Print, then Done once the agent has the run. */
  const actions = (count: number, onPrint: () => void) => ({
    secondaryAction: send.isSuccess ? undefined : (
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    ),
    primaryAction: send.isSuccess ? (
      <Button variant="primary" onClick={onClose}>
        Done
      </Button>
    ) : (
      <Button
        variant="primary"
        busy={send.isPending}
        disabledReason={
          count === 0
            ? 'Nothing to print'
            : route.kind === 'looking'
              ? 'Looking for this computer’s print agent'
              : undefined
        }
        onClick={onPrint}
      >
        Print {labelsWord(count)}
      </Button>
    ),
  });

  return { route, send, print, throughBrowser, actions };
}

function labelsWord(count: number): string {
  return `${count} label${count === 1 ? '' : 's'}`;
}

/** What happened, or which way it will go — at the top of either dialog. */
export function LabelRunFeedback({
  run,
  count,
  onBrowser,
}: {
  run: ReturnType<typeof useLabelRun>;
  count: number;
  onBrowser: () => void;
}) {
  if (run.send.isSuccess) {
    return (
      <Callout tone="success" title={`Sent ${labelsWord(count)} to ${run.send.data.printer}`}>
        They print as one run. If the printer stops partway, check the roll, then open Print labels
        again for the rest.
      </Callout>
    );
  }
  return (
    <>
      {run.send.isError ? (
        <Callout tone="danger" title="The labels did not go to the printer">
          {run.send.error.message}{' '}
          <Button variant="secondary" onClick={onBrowser}>
            Print through the browser instead
          </Button>
        </Callout>
      ) : null}
      <RouteNote route={run.route} />
    </>
  );
}

/** Which way a run will print. */
export type LabelRoute =
  | { readonly kind: 'browser' }
  | { readonly kind: 'looking' }
  | { readonly kind: 'agent'; readonly printer: string }
  | { readonly kind: 'agent-missing'; readonly reason: string }
  | { readonly kind: 'agent-unset' };

/**
 * Through this computer's print agent, or through the browser — decided
 * before the press and said on the dialog, never discovered afterwards
 * (decision 130).
 *
 * The agent is asked only on a computer that has been set up for it (see
 * `printAgentEnabled`), and only for roll stock; sheet labels are a page for
 * an office printer and always go through the browser. When the agent should
 * be there and is not, the run still prints — through the browser — and the
 * dialog says why, because a label run that cannot happen at all is a dead end.
 */
export function useLabelRoute(size: LabelSize): LabelRoute {
  const wanted = printAgentEnabled() && zplSupports(size);
  const status = useQuery({
    queryKey: ['print-agent', 'status'],
    queryFn: agentStatus,
    enabled: wanted,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  if (!wanted) return { kind: 'browser' };
  if (status.isPending) return { kind: 'looking' };
  if (status.isError) return { kind: 'agent-missing', reason: status.error.message };
  if (status.data.labelPrinter === null) return { kind: 'agent-unset' };
  return { kind: 'agent', printer: status.data.labelPrinter };
}

function RouteNote({ route }: { route: LabelRoute }) {
  switch (route.kind) {
    case 'agent':
      return (
        <p className="text-secondary">
          Prints on {route.printer}, through this computer’s print agent.
        </p>
      );
    case 'agent-missing':
      return (
        <Callout tone="warning" title="Printing through the browser this time">
          {route.reason}
        </Callout>
      );
    case 'agent-unset':
      return (
        <Callout tone="warning" title="Printing through the browser this time">
          No label printer is chosen for this computer’s print agent. An admin chooses it in
          Settings, under Label printer on this computer.
        </Callout>
      );
    default:
      return null;
  }
}

/**
 * Said before the run, not discovered at the register (§2.2-7, §7).
 *
 * A warning rather than a refusal: a shop with a marginal label is better off
 * than a shop with none, and the remedy — different stock, a shorter code — is
 * not always available at eleven at night. The figure is there because "1.5 dots
 * a bar" says what to do and "unscannable" does not (§2.2-10). Absent entirely
 * when there is nothing to say (§2.2-13).
 *
 * Its own component because two screens print labels now, and a warning that
 * only one of them shows is the shape this codebase keeps paying for.
 */
export function ScannabilityNotice({
  items,
  size,
  throughAgent = false,
}: {
  items: readonly LabelItem[];
  size: LabelSize;
  /** Through the print agent the barcode is drawn differently — judge that one. */
  throughAgent?: boolean;
}) {
  const codes = items.map(codeFor);
  const tooDense = throughAgent ? unscannableOnAgent(codes, size) : unscannableCodes(codes, size);
  const worst = tooDense[0];
  if (worst === undefined) return null;

  return (
    <Callout tone="warning" title={`${tooDense.length} of these may not scan`}>
      {worst.moduleDots === null
        ? `${worst.code} cannot be drawn as a barcode at all, so those labels print without one.`
        : `On ${LABEL_SIZES[size].label} stock the bars come out ${worst.moduleDots.toFixed(1)} printer dots wide, and a thermal printer needs ${MIN_SCANNABLE_MODULE_DOTS}. Longest code: ${worst.code}. Larger stock, or a shorter code, fixes it — the number under the barcode is still readable either way.`}
    </Callout>
  );
}
